import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import ScrollableTimeline from './ScrollableTimeline';

// Single source of truth: a signup is "active" if it counts toward slot capacity
// and represents a volunteer who is still expected to show up.
const isActiveSignup = (s) => s.status !== 'denied' && s.status !== 'cancelled';

// Lays out timeslots in the daily view: each department gets one or more horizontal
// "tracks" (rows). Non-overlapping slots within a department share a row; overlapping
// ones get extra sub-rows. Departments sort alphabetically.
function computeDeptTracks(slots, getDeptName) {
  const byDept = {};
  for (const s of slots) {
    (byDept[s.department_id] = byDept[s.department_id] || []).push(s);
  }
  const sortedDeptIds = Object.keys(byDept)
    .map(id => Number(id))
    .sort((a, b) => getDeptName(a).localeCompare(getDeptName(b)));

  const slotRow = {};
  let totalRows = 0;

  for (const deptId of sortedDeptIds) {
    const deptSlots = [...byDept[deptId]].sort(
      (a, b) => new Date(a.start_time) - new Date(b.start_time)
    );
    const trackEnds = [];
    for (const slot of deptSlots) {
      const start = new Date(slot.start_time).getTime();
      const end = new Date(slot.end_time).getTime();
      let trackIdx = trackEnds.findIndex(e => e <= start);
      if (trackIdx === -1) {
        trackEnds.push(end);
        trackIdx = trackEnds.length - 1;
      } else {
        trackEnds[trackIdx] = end;
      }
      slotRow[slot.id] = totalRows + trackIdx + 1;
    }
    totalRows += trackEnds.length;
  }

  return { slotRow, totalRows };
}

function AdminSignupView({ username }) {
  const [timeslots, setTimeslots] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [signups, setSignups] = useState({});
  // Every signup (including denied/cancelled), grouped by slot. Powers the
  // super-admin override controls in the slot modal — where a denied signup
  // still needs to be visible so it can be re-approved. `signups` above stays
  // active-only because it drives capacity counts and colors everywhere else.
  const [allSignups, setAllSignups] = useState({});
  const [volunteers, setVolunteers] = useState([]);
  const [viewMode, setViewMode] = useState('daily'); // 'daily', 'table', 'volunteers'
  const [filters, setFilters] = useState({
    departmentId: '',
    date: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });
  const [volSearch, setVolSearch] = useState('');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [bulkRes, volunteersRes] = await Promise.all([
        axios.get(`${API_URL}/timeslots/admin/data`),
        axios.get(`${API_URL}/volunteers`)
      ]);

      setTimeslots(bulkRes.data.timeslots);
      setDepartments(bulkRes.data.departments);
      setVolunteers(volunteersRes.data);

      // Only active signups (not denied/cancelled) drive capacity counts and
      // the table/daily views. Cancelled volunteers are no longer attending.
      // We also keep an all-inclusive map for the super-admin override modal.
      const signupsData = {};
      const allSignupsData = {};
      for (const slot of bulkRes.data.timeslots) {
        signupsData[slot.id] = [];
        allSignupsData[slot.id] = [];
      }
      for (const s of bulkRes.data.signups) {
        if (allSignupsData[s.timeslot_id]) allSignupsData[s.timeslot_id].push(s);
        if (isActiveSignup(s) && signupsData[s.timeslot_id]) {
          signupsData[s.timeslot_id].push(s);
        }
      }
      setSignups(signupsData);
      setAllSignups(allSignupsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDepartmentName = (id) => {
    const dept = departments.find(d => d.id === id);
    return dept ? dept.name : 'Unknown';
  };

  const formatTimeRange = (startTimeStr, endTimeStr) => {
    const startDate = new Date(startTimeStr);
    const endDate = new Date(endTimeStr);

    const startMonth = startDate.toLocaleString('en-US', { month: 'short' });
    const startDay = startDate.getDate();
    const startTime = startDate.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    const endMonth = endDate.toLocaleString('en-US', { month: 'short' });
    const endDay = endDate.getDate();
    const endTime = endDate.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    // Check if it's overnight (spans multiple days)
    if (startDay !== endDay) {
      return `${startMonth} ${startDay} ${startTime} - ${endMonth} ${endDay} ${endTime}`;
    } else {
      return `${startMonth} ${startDay} ${startTime} - ${endTime}`;
    }
  };

  const getFilteredSlots = () => {
    let filtered = timeslots;

    if (filters.departmentId) {
      const filterDeptId = parseInt(filters.departmentId);
      const filterDept = departments.find(d => d.id === filterDeptId);
      // If the selected dept is an on-call viewer (e.g. Con Ops), mirror what
      // its own admin sees: that dept's slots plus every cross-dept on-call
      // slot. Otherwise, just the dept's own slots.
      filtered = filterDept?.is_on_call_viewer
        ? filtered.filter(slot => slot.department_id === filterDeptId || slot.on_call)
        : filtered.filter(slot => slot.department_id === filterDeptId);
    }

    if (filters.date) {
      filtered = filtered.filter(slot => {
        const slotDate = new Date(slot.start_time).toDateString();
        const filterDate = new Date(filters.date).toDateString();
        return slotDate === filterDate;
      });
    }

    // Filter by volunteer name if name filter is provided
    if (filters.name.trim()) {
      const searchName = filters.name.toLowerCase().trim();
      filtered = filtered.filter(slot => {
        const slotSignups = signups[slot.id] || [];
        return slotSignups.some(signup => 
          signup.name.toLowerCase().includes(searchName)
        );
      });
    }

    return filtered.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  };

  const filteredSlots = getFilteredSlots();

  return (
    <div className="signup-view-container">
      <h2>Volunteer Signups</h2>

      {/* Controls */}
      <div className="signup-controls">
        <div className="controls-row">
          <div className="form-group">
            <label>Filter by Department</label>
            <select
              value={filters.departmentId}
              onChange={(e) => setFilters(prev => ({ ...prev, departmentId: e.target.value }))}
            >
              <option value="">All Departments</option>
              {[...departments].sort((a, b) => a.name.localeCompare(b.name)).map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Filter by Date</label>
            <input
              type="date"
              value={filters.date}
              onChange={(e) => setFilters(prev => ({ ...prev, date: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label>Search by Name</label>
            <input
              type="text"
              placeholder="Type volunteer name..."
              value={filters.name}
              onChange={(e) => setFilters(prev => ({ ...prev, name: e.target.value }))}
            />
          </div>

          <div className="view-toggle">
            <button
              className={viewMode === 'daily' ? 'active' : ''}
              onClick={() => setViewMode('daily')}
            >
              Daily
            </button>
            <button
              className={viewMode === 'table' ? 'active' : ''}
              onClick={() => setViewMode('table')}
            >
              Table
            </button>
            <button
              className={viewMode === 'volunteers' ? 'active' : ''}
              onClick={() => setViewMode('volunteers')}
            >
              Volunteers
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <p>Loading signups...</p>
      ) : viewMode === 'volunteers' ? (
        <VolunteerView
          volunteers={volunteers}
          timeslots={timeslots}
          departments={departments}
          signups={signups}
          search={volSearch}
          onSearchChange={setVolSearch}
          onChanged={fetchAllData}
        />
      ) : viewMode === 'daily' ? (
        <DailyView
          slots={filteredSlots}
          signups={signups}
          allSignups={allSignups}
          getDepartmentName={getDepartmentName}
          formatTimeRange={formatTimeRange}
          username={username}
          onChanged={fetchAllData}
        />
      ) : (
        <TableView
          slots={filteredSlots}
          signups={signups}
          getDepartmentName={getDepartmentName}
          formatTimeRange={formatTimeRange}
          sortConfig={sortConfig}
          onSort={(column) => {
            setSortConfig(prev => ({
              column,
              direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
            }));
          }}
        />
      )}
    </div>
  );
}

// Table View Component
function TableView({ slots, signups, getDepartmentName, formatTimeRange, sortConfig, onSort }) {
  const allSignups = [];
  
  slots.forEach(slot => {
    (signups[slot.id] || []).forEach(signup => {
      allSignups.push({
        ...signup,
        slotId: slot.id,
        department: getDepartmentName(slot.department_id),
        date: new Date(slot.start_time).toLocaleDateString(),
        time: formatTimeRange(slot.start_time, slot.end_time)
      });
    });
  });

  // Sort signups based on sortConfig
  const sortedSignups = [...allSignups].sort((a, b) => {
    if (!sortConfig.column) return 0;

    let aValue = a[sortConfig.column];
    let bValue = b[sortConfig.column];

    // Handle string comparisons (case-insensitive)
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const getSortIndicator = (column) => {
    if (sortConfig.column !== column) return ' ⇅';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const exportCSV = () => {
    const headers = ['Name', 'Phone', 'Department', 'Date', 'Shift'];
    const dataRows = sortedSignups.map(r => [r.name, r.phone, r.department, r.date, r.time]);
    const esc = (v) => {
      let s = String(v ?? '');
      if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
      return `"${s.replace(/"/g, '""')}"`;
    };
    const csv = [headers, ...dataRows].map(row => row.map(esc).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `volunteer-signups-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="table-view">
      {allSignups.length === 0 ? (
        <div className="no-data">No signups to display</div>
      ) : (
        <>
          <div className="table-export-row">
            <span className="table-count">{sortedSignups.length} record{sortedSignups.length !== 1 ? 's' : ''}</span>
            <button className="report-export-btn" onClick={exportCSV}>EXPORT CSV</button>
          </div>
          <table className="signups-table">
            <thead>
              <tr>
                <th
                  onClick={() => onSort('name')}
                  style={{ cursor: 'pointer' }}
                  className={sortConfig.column === 'name' ? 'sorted' : ''}
                >
                  Name{getSortIndicator('name')}
                </th>
                <th
                  onClick={() => onSort('email')}
                  style={{ cursor: 'pointer' }}
                  className={sortConfig.column === 'email' ? 'sorted' : ''}
                >
                  Number{getSortIndicator('email')}
                </th>
                <th
                  onClick={() => onSort('department')}
                  style={{ cursor: 'pointer' }}
                  className={sortConfig.column === 'department' ? 'sorted' : ''}
                >
                  Department{getSortIndicator('department')}
                </th>
                <th
                  onClick={() => onSort('date')}
                  style={{ cursor: 'pointer' }}
                  className={sortConfig.column === 'date' ? 'sorted' : ''}
                >
                  Date{getSortIndicator('date')}
                </th>
                <th
                  onClick={() => onSort('time')}
                  style={{ cursor: 'pointer' }}
                  className={sortConfig.column === 'time' ? 'sorted' : ''}
                >
                  Time{getSortIndicator('time')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedSignups.map((signup, idx) => (
                <tr key={`${signup.slotId}-${signup.id}`} className={idx % 2 === 0 ? 'even' : 'odd'}>
                  <td>{signup.name}</td>
                  <td className="phone-cell">{signup.phone}</td>
                  <td>{signup.department}</td>
                  <td>{signup.date}</td>
                  <td>{signup.time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

// Daily View Component - Color-coded timeline by day
function DailyView({ slots, signups, allSignups = {}, getDepartmentName, formatTimeRange, username, onChanged }) {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [editingSignup, setEditingSignup] = useState(null);
  const [editTimes, setEditTimes] = useState({ start: '', end: '' });
  const [actionError, setActionError] = useState('');
  const [busyId, setBusyId] = useState(null);

  // Super-admin overrides: approve/deny work regardless of the slot's end time
  // or a prior dept-admin decision. The backend permits re-deciding an already
  // approved/denied signup, so these buttons are the UI surface for that.
  const handleApprove = async (signupId) => {
    setActionError('');
    setBusyId(signupId);
    try {
      await axios.post(`${API_URL}/timeslots/signup/${signupId}/approve`, { approved_by: username });
      onChanged && onChanged();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Failed to approve');
    } finally {
      setBusyId(null);
    }
  };

  const handleDeny = async (signupId) => {
    setActionError('');
    setBusyId(signupId);
    try {
      await axios.post(`${API_URL}/timeslots/signup/${signupId}/deny`, { approved_by: username });
      onChanged && onChanged();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Failed to deny');
    } finally {
      setBusyId(null);
    }
  };

  const toDatetimeLocal = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.replace(' ', 'T').slice(0, 16);
  };

  const openEditModal = (signup, slot) => {
    setActionError('');
    setEditingSignup(signup);
    setEditTimes({
      start: toDatetimeLocal(signup.actual_start_time || slot?.start_time),
      end: toDatetimeLocal(signup.actual_end_time || slot?.end_time)
    });
  };

  const handleEditTimes = async () => {
    if (!editingSignup) return;
    try {
      await axios.put(`${API_URL}/timeslots/signup/${editingSignup.id}`, {
        actual_start_time: editTimes.start,
        actual_end_time: editTimes.end
      });
      setEditingSignup(null);
      onChanged && onChanged();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Failed to save times');
    }
  };

  if (slots.length === 0) {
    return <div className="no-data">No timeslots to display</div>;
  }

  // Group slots by date — overnight shifts (cross-midnight) appear on BOTH days.
  // Each side is tagged so the bubble can render a continuation hint:
  //   _wrap='start' on day 1 (arrow points forward into the next day)
  //   _wrap='end'   on day 2 (arrow points back to the prior day)
  const slotsByDate = {};
  slots.forEach(slot => {
    const startDay = (slot.start_time || '').slice(0, 10);
    const endDay = (slot.end_time || '').slice(0, 10);
    if (!startDay) return;
    const wraps = endDay && endDay !== startDay;
    (slotsByDate[startDay] = slotsByDate[startDay] || []).push(
      wraps ? { ...slot, _wrap: 'start' } : slot
    );
    if (wraps) {
      (slotsByDate[endDay] = slotsByDate[endDay] || []).push({ ...slot, _wrap: 'end' });
    }
  });

  // Sort dates lexicographically (works for YYYY-MM-DD strings)
  const sortedDates = Object.keys(slotsByDate).sort();

  const getCapacityColor = (slot) => {
    const filled = (signups[slot.id] || []).length;
    const percentage = (filled / slot.max_volunteers) * 100;
    
    if (percentage === 0) return 'empty';
    if (percentage < 50) return 'low';
    if (percentage < 100) return 'medium';
    return 'full';
  };

  const getMinuteRange = (startTime, endTime) => {
    // Parse H:M directly from "YYYY-MM-DDTHH:MM:SS" — no timezone juggling.
    // Return minutes-from-midnight so bubbles land on the right minute, not
    // just the nearest hour (a 9:30 slot used to render as 9:00).
    const sH = parseInt((startTime || '').slice(11, 13), 10) || 0;
    const sM = parseInt((startTime || '').slice(14, 16), 10) || 0;
    const eH = parseInt((endTime || '').slice(11, 13), 10) || 0;
    const eM = parseInt((endTime || '').slice(14, 16), 10) || 0;
    const start = sH * 60 + sM;
    let end = eH * 60 + eM;
    // If end <= start, the shift crosses midnight (e.g. 23:00 → 02:00).
    if (end <= start) end += 24 * 60;
    return { start, end };
  };

  return (
    <div className="daily-view">
      {sortedDates.map(date => (
        <div key={date} className="daily-schedule">
          <div className="schedule-date">
            {/* Anchor to noon local time — bare 'YYYY-MM-DD' parses as UTC midnight,
                which renders as the previous evening in Eastern time. */}
            {new Date(date + 'T12:00:00').toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric'
            })}
          </div>
          
          <ScrollableTimeline>
            <div className="hours-labels">
              {Array.from({ length: 24 }, (_, hour) => {
                const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                const period = hour >= 12 ? 'p' : 'a';
                return (
                  <div
                    key={hour}
                    className="hour-label"
                    style={{ left: `${(hour * 100) / 24}%` }}
                  >
                    {displayHour}{period}
                  </div>
                );
              })}
            </div>

            {(() => {
              const { slotRow, totalRows } = computeDeptTracks(slotsByDate[date], getDepartmentName);
              // With 5+ department tracks stacked, it's hard to eyeball which
              // hour a bubble sits under. Render faint dashed verticals every
              // other hour as alignment guides for super admins.
              const showGuides = totalRows > 4;
              return (
                <div
                  className="timeline-slots"
                  style={{ gridTemplateRows: `repeat(${totalRows}, minmax(40px, auto))` }}
                >
                  {showGuides && [2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22].map(hour => (
                    <div
                      key={`guide-${hour}`}
                      className="timeline-guide"
                      style={{
                        gridColumn: `${hour * 60 + 1} / span 1`,
                        gridRow: `1 / -1`,
                      }}
                      aria-hidden="true"
                    />
                  ))}
                  {slotsByDate[date].map(slot => {
                    const { start, end } = getMinuteRange(slot.start_time, slot.end_time);
                    // Grid is 1440 cols (one per minute of the day).
                    // For wrap='end' (continuation row on day 2), show 00:00 → end-of-shift.
                    // Otherwise show start-of-shift → end of day (capped at 1440).
                    const DAY = 24 * 60;
                    const colStart = slot._wrap === 'end' ? 0 : start;
                    const colEnd = slot._wrap === 'end' ? Math.min(end - DAY, DAY) : Math.min(end, DAY);
                    const duration = Math.max(colEnd - colStart, 1);
                    const capacityColor = getCapacityColor(slot);
                    const filled = (signups[slot.id] || []).length;
                    return (
                      <div
                        key={`${slot.id}-${slot._wrap || 'start'}`}
                        className={`timeline-slot ${capacityColor} ${slot._wrap ? 'slot-wrap-' + slot._wrap : ''}`}
                        style={{
                          gridColumn: `${colStart + 1} / span ${duration}`,
                          gridRow: slotRow[slot.id],
                          position: 'relative'
                        }}
                        title={`${getDepartmentName(slot.department_id)} - ${filled}/${slot.max_volunteers} volunteers`}
                        onClick={() => setSelectedSlot(slot)}
                      >
                        <div className="slot-label">
                          <div className="slot-dept">{getDepartmentName(slot.department_id)}</div>
                          <div className="slot-capacity">{filled}/{slot.max_volunteers}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </ScrollableTimeline>

          <div className="schedule-legend">
            <div className="legend-item">
              <div className="legend-color empty"></div>
              <span>No Signups</span>
            </div>
            <div className="legend-item">
              <div className="legend-color low"></div>
              <span>0-50% Full</span>
            </div>
            <div className="legend-item">
              <div className="legend-color medium"></div>
              <span>50-100% Full</span>
            </div>
            <div className="legend-item">
              <div className="legend-color full"></div>
              <span>Full</span>
            </div>
          </div>
        </div>
      ))}

      {selectedSlot && (() => {
        // Show every signup (active first, then denied/cancelled) so a super
        // admin can override any prior decision. The header count reflects only
        // active volunteers, matching capacity everywhere else.
        const all = allSignups[selectedSlot.id] || signups[selectedSlot.id] || [];
        const ordered = [...all].sort(
          (a, b) => (isActiveSignup(b) ? 1 : 0) - (isActiveSignup(a) ? 1 : 0)
        );
        const activeCount = all.filter(isActiveSignup).length;
        const timeLabel = formatTimeRange
          ? formatTimeRange(selectedSlot.start_time, selectedSlot.end_time)
          : `${new Date(selectedSlot.start_time).toLocaleString()} – ${new Date(selectedSlot.end_time).toLocaleString()}`;
        return (
          <div className="slot-modal-overlay" onClick={() => setSelectedSlot(null)}>
            <div className="slot-modal" onClick={e => e.stopPropagation()}>
              <div className="slot-modal-header">
                <div>
                  <h3>{getDepartmentName(selectedSlot.department_id)}</h3>
                  <div className="slot-modal-time">{timeLabel}</div>
                </div>
                <button className="slot-modal-close" onClick={() => setSelectedSlot(null)}>✕</button>
              </div>
              <div className="slot-modal-body">
                {selectedSlot.notes && (
                  <div className="slot-modal-notes">{selectedSlot.notes}</div>
                )}
                <div className="slot-modal-count">
                  {activeCount} / {selectedSlot.max_volunteers} volunteers
                </div>
                {actionError && <div className="assign-modal-error">{actionError}</div>}
                {ordered.length === 0 ? (
                  <div className="slot-modal-empty">No signups for this slot yet.</div>
                ) : (
                  ordered.map((vol, idx) => (
                    <div
                      key={vol.id}
                      className={`slot-modal-volunteer ${isActiveSignup(vol) ? '' : 'is-inactive'}`}
                    >
                      <div className="slot-modal-vol-number">{idx + 1}</div>
                      <div className="slot-modal-vol-main">
                        <div className="slot-modal-vol-name">
                          {vol.name}
                          <span className={`signup-status-badge status-${vol.status}`}>{vol.status}</span>
                        </div>
                        <div className="slot-modal-vol-phone">{vol.phone}</div>
                        <div className="slot-modal-vol-actions">
                          {vol.status !== 'approved' && (
                            <button
                              className="action-btn approve-btn"
                              disabled={busyId === vol.id}
                              onClick={() => handleApprove(vol.id)}
                            >
                              APPROVE
                            </button>
                          )}
                          {vol.status !== 'denied' && (
                            <button
                              className="action-btn deny-btn"
                              disabled={busyId === vol.id}
                              onClick={() => handleDeny(vol.id)}
                            >
                              DENY
                            </button>
                          )}
                          <button
                            className="action-btn edit-btn"
                            onClick={() => openEditModal(vol, selectedSlot)}
                          >
                            EDIT
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {editingSignup && (
        <div className="edit-time-modal-overlay" onClick={() => setEditingSignup(null)}>
          <div className="edit-time-modal" onClick={e => e.stopPropagation()}>
            <div className="edit-time-modal-header">
              <h3 className="edit-time-modal-title">Edit Shift Times</h3>
              <button className="edit-time-modal-close" onClick={() => setEditingSignup(null)}>✕</button>
            </div>
            <p className="edit-time-modal-name">{editingSignup.name}</p>
            {actionError && <div className="assign-modal-error">{actionError}</div>}
            <div className="edit-time-fields">
              <label className="edit-time-label">Start</label>
              <input
                className="edit-time-input"
                type="datetime-local"
                value={editTimes.start}
                onChange={e => setEditTimes(t => ({ ...t, start: e.target.value }))}
              />
              <label className="edit-time-label">End</label>
              <input
                className="edit-time-input"
                type="datetime-local"
                value={editTimes.end}
                onChange={e => setEditTimes(t => ({ ...t, end: e.target.value }))}
              />
            </div>
            <div className="edit-time-actions">
              <button className="action-btn deny-btn" onClick={() => setEditingSignup(null)}>CANCEL</button>
              <button className="action-btn approve-btn" onClick={handleEditTimes}>SAVE</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VolunteerView({ volunteers, timeslots = [], departments = [], signups = {}, search, onSearchChange, onChanged }) {
  const [assignTarget, setAssignTarget] = useState(null);

  const filtered = volunteers.filter(v =>
    v.name.toLowerCase().includes(search.toLowerCase()) ||
    v.lookup_code.includes(search.toUpperCase())
  );

  const exportCSV = () => {
    const headers = ['Code', 'Name', 'Phone', 'Shifts'];
    const rows = filtered.map(v => [v.lookup_code, v.name, v.phone, v.signup_count]);
    const esc = (val) => {
      let s = String(val ?? '');
      if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
      return `"${s.replace(/"/g, '""')}"`;
    };
    const csv = [headers, ...rows].map(r => r.map(esc).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `volunteers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="table-view">
      <div className="table-export-row">
        <input
          type="text"
          className="vol-search-input"
          placeholder="Search by name or code…"
          value={search}
          onChange={e => onSearchChange(e.target.value)}
        />
        <span className="table-count">{filtered.length} volunteer{filtered.length !== 1 ? 's' : ''}</span>
        <button className="report-export-btn" onClick={exportCSV}>EXPORT CSV</button>
      </div>

      {filtered.length === 0 ? (
        <div className="no-data">No volunteers found.</div>
      ) : (
        <table className="signups-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>Shifts</th>
              <th>Code</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((v, idx) => (
              <tr key={v.id} className={idx % 2 === 0 ? 'even' : 'odd'}>
                <td>{v.name}</td>
                <td className="phone-cell">{v.phone}</td>
                <td>{v.signup_count}</td>
                <td><span className="vol-code-badge">{v.lookup_code}</span></td>
                <td>
                  <button className="action-btn edit-btn" onClick={() => setAssignTarget(v)}>
                    ASSIGN
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {assignTarget && (
        <AssignShiftModal
          volunteer={assignTarget}
          timeslots={timeslots}
          departments={departments}
          signups={signups}
          onClose={() => setAssignTarget(null)}
          onAssigned={() => { setAssignTarget(null); onChanged && onChanged(); }}
        />
      )}
    </div>
  );
}

function AssignShiftModal({ volunteer, timeslots, departments, signups, onClose, onAssigned }) {
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deptFilter, setDeptFilter] = useState('');

  const volunteerSlotIds = new Set(
    Object.values(signups).flat()
      .filter(s => s.volunteer_id === volunteer.id && s.status !== 'denied' && s.status !== 'cancelled')
      .map(s => s.timeslot_id)
  );

  const eligibleSlots = timeslots
    .filter(slot => {
      const slotSignups = signups[slot.id] || [];
      const active = slotSignups.filter(s => s.status !== 'denied' && s.status !== 'cancelled').length;
      if (active >= slot.max_volunteers) return false;
      if (volunteerSlotIds.has(slot.id)) return false;
      if (deptFilter && slot.department_id !== parseInt(deptFilter)) return false;
      return true;
    })
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

  const getDeptName = (id) => departments.find(d => d.id === id)?.name || 'Unknown';

  const formatTime = (startStr, endStr) => {
    const s = new Date(startStr);
    const e = new Date(endStr);
    const opts = { hour: 'numeric', minute: '2-digit', hour12: true };
    const date = s.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    return `${date} • ${s.toLocaleString('en-US', opts)} – ${e.toLocaleString('en-US', opts)}`;
  };

  const handleAssign = async (slotId) => {
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/timeslots/${slotId}/assign`, {
        lookup_code: volunteer.lookup_code
      });
      onAssigned();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to assign volunteer');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="assign-modal-overlay" onClick={onClose}>
      <div className="assign-modal" onClick={e => e.stopPropagation()}>
        <div className="assign-modal-header">
          <h3 className="assign-modal-title">Assign to Shift</h3>
          <button className="assign-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="assign-modal-volunteer">
          <strong>{volunteer.name}</strong>
          <span className="vol-code-badge">{volunteer.lookup_code}</span>
        </div>

        <div className="assign-modal-filter">
          <label>Department</label>
          <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
            <option value="">All Departments</option>
            {[...departments].sort((a, b) => a.name.localeCompare(b.name)).map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>

        {error && <div className="assign-modal-error">{error}</div>}

        <div className="assign-modal-slots">
          {eligibleSlots.length === 0 ? (
            <div className="no-data">No eligible shifts available.</div>
          ) : (
            eligibleSlots.map(slot => {
              const active = (signups[slot.id] || []).filter(s => s.status !== 'denied' && s.status !== 'cancelled').length;
              return (
                <button
                  key={slot.id}
                  className="assign-slot-card"
                  disabled={submitting}
                  onClick={() => handleAssign(slot.id)}
                >
                  <div className="assign-slot-dept">{getDeptName(slot.department_id)}</div>
                  <div className="assign-slot-time">{formatTime(slot.start_time, slot.end_time)}</div>
                  <div className="assign-slot-cap">{active} / {slot.max_volunteers} filled</div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminSignupView;
